import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  TextField,
  LinearProgress,
  Alert,
  CircularProgress,
  Checkbox,
  Divider,
  useTheme,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Modal,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NovaMatchResult from "../components/NovaMatchResult";
import ScanHistoryModal from "../components/ScanHistoryModal";
import ScanHistoryDropdown from "../components/ScanHistoryDropdown";
import ProfessionalSidebar from "../components/ProfessionalSidebar";
import SkillHighlighting from "../components/SkillHighlighting";
import ValidationErrorDisplay from "../components/ValidationErrorDisplay";
import { novaMatchAPI } from "../services/api";
import authService from "../services/auth";
import { useNavigate, useLocation } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import { Menu as MenuIcon, Close as CloseIcon, ArrowDropDown, Payment, Star, Receipt, HelpOutline } from "@mui/icons-material";

/** Align with backend `MAX_RESUME_FILE_SIZE_MB` default (`backend/routes/userResumeRoutes.js`). */
const MAX_RESUME_UPLOAD_MB = 25;
const MAX_RESUME_UPLOAD_BYTES = MAX_RESUME_UPLOAD_MB * 1024 * 1024;

// Add CSS animations
const styles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

// Normalize legacy v1 analysis into v2 shape for UI
function normalizeAnalysisV2(result) {
  if (!result) return null;
  if (result.scores && result.keywordMatches && result.keywords) return result; // already v2
  // v1 → v2 adapter
  if (typeof result.hireabilityScore === 'number') {
    const mustHave = Array.isArray(result.mustHaveSkills) ? result.mustHaveSkills : [];
    const covered = mustHave.filter(m => m.present).map(m => m.skill);
    const missing = mustHave.filter(m => !m.present).map(m => m.skill);
    const total = mustHave.length || 1;
    const coveragePercent = Math.round((covered.length / total) * 100);
    return {
      candidateMetadata: { name: '', email: null, phone: null, location: null, linkedin: null, github: null, education: null, totalYearsExperience: null },
      keywords: { jdMustHave: mustHave.map(m => m.skill), jdNiceToHave: [], resumeSkills: [] },
      keywordMatches: { mustHaveCovered: covered, mustHaveMissing: missing, niceToHaveCovered: [], coveragePercent },
      scores: { keywordCoverage: coveragePercent, experienceFit: 50, overall: result.hireabilityScore },
      eligibilityReasoning: result.summary || '',
      riskFlags: Array.isArray(result.criticalGaps) ? result.criticalGaps : [],
      recommendations: [],
      interviewQuestions: [],
      raw: { extractedTextHash: '', model: 'legacy-v1', promptVersion: '1.0' },
    };
  }
  return result;
}

// Analysis steps for progressive loading
const ANALYSIS_STEPS = [
  { label: 'Extracting basic information', icon: '📄' },
  { label: 'Building experience timeline', icon: '📅' },
  { label: 'Analyzing skills match', icon: '🎯' },
  { label: 'Calculating scores', icon: '📊' },
  { label: 'Generating insights', icon: '💡' },
  { label: 'Finalizing recommendations', icon: '👔' }
];

const BATCH_LOADING_STEPS = [
  'Preparing request',
  'Uploading and linking resumes',
  'Running AI analysis',
  'Building candidate ranking',
  'Finalizing results'
];

const BATCH_STAGE_LABELS = {
  validating: 'Validating request',
  loading_resumes: 'Preparing resumes',
  analyzing_resume: 'Running AI analysis',
  ranking: 'Ranking candidates',
  saving_history: 'Saving scan history',
  complete: 'Completed',
  error: 'Error'
};

const NovaMatch = () => {
  const [analysisMode, setAnalysisMode] = useState(null); // null | 'single' | 'batch'
  const [inputType, setInputType] = useState(0); // 0 = File upload, 1 = Text input
  const [resume, setResume] = useState(null);
  const [resumeText, setResumeText] = useState("");
  const [resumePreview, setResumePreview] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [batchComparison, setBatchComparison] = useState(null);
  const [batchSelectedResumeId, setBatchSelectedResumeId] = useState(null);
  const [showBatchSelectModal, setShowBatchSelectModal] = useState(false);
  const [resumeLibrary, setResumeLibrary] = useState([]);
  const [resumeLibraryLoading, setResumeLibraryLoading] = useState(false);
  const [resumeLibraryError, setResumeLibraryError] = useState(null);
  const [batchSelectedResumeIds, setBatchSelectedResumeIds] = useState([]);
  const [batchUploadFiles, setBatchUploadFiles] = useState([]);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchProgressMessage, setBatchProgressMessage] = useState('');
  const [batchStage, setBatchStage] = useState('');
  const [batchProgressMetrics, setBatchProgressMetrics] = useState({
    processedCount: 0,
    totalCount: 0,
    currentResumeName: ''
  });
  const [batchElapsedMs, setBatchElapsedMs] = useState(0);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  // Auto-collapse sidebar on mobile
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [showDocAnalysis, setShowDocAnalysis] = useState(false);
  const [showInputSections, setShowInputSections] = useState(true);
  const [subscriptionAnchorEl, setSubscriptionAnchorEl] = useState(null);
  const subscriptionButtonRef = useRef(null);
  const isTogglingRef = useRef(false);
  const batchAbortRef = useRef(null);
  const batchTimerRef = useRef(null);
  const batchStartedAtRef = useRef(null);
  const [remainingAnalyses, setRemainingAnalyses] = useState(null);
  const [userPlan, setUserPlan] = useState('Basic');
  const [subscriptionActive, setSubscriptionActive] = useState(true);
  const [userEmail, setUserEmail] = useState(null);
  const [isNovalinkEmployee, setIsNovalinkEmployee] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();


  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  const handleSubscriptionMenuOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    
    // Set flag to indicate we're intentionally toggling
    isTogglingRef.current = true;
    
    // Toggle menu state
    if (subscriptionAnchorEl) {
      // Menu is open, close it
      setSubscriptionAnchorEl(null);
      subscriptionButtonRef.current = null;
    } else {
      // Menu is closed, open it
      setSubscriptionAnchorEl(event.currentTarget);
      subscriptionButtonRef.current = event.currentTarget;
    }
    
    // Reset flag after a short delay to allow event propagation to complete
    setTimeout(() => {
      isTogglingRef.current = false;
    }, 100);
  };

  const handleSubscriptionMenuClose = (event, reason) => {
    // Don't close if we're intentionally toggling via button click
    if (isTogglingRef.current) {
      return;
    }
    
    // Don't close if clicking the button itself
    if (event && subscriptionButtonRef.current) {
      const target = event.target;
      
      // Check if the click originated from the button or its children
      if (
        subscriptionButtonRef.current === target ||
        subscriptionButtonRef.current.contains(target) ||
        (target && target.closest && target.closest('button') === subscriptionButtonRef.current)
      ) {
        // Click was on the button, don't close
        return;
      }
    }
    
    // Close the menu for backdrop clicks, escape key, or other reasons
    setSubscriptionAnchorEl(null);
    subscriptionButtonRef.current = null;
  };

  const handleBillingClick = () => {
    handleSubscriptionMenuClose();
    navigate('/billing');
  };

  const handleHelpClick = () => {
    handleSubscriptionMenuClose();
    navigate('/support');
  };

  const handleInvoiceClick = () => {
    handleSubscriptionMenuClose();
    navigate('/invoice');
  };

  useEffect(() => {
    loadUserEmail();
    loadRemainingAnalyses();
    checkSubscriptionStatus();
    
    const handleStorageChange = (e) => {
      if (e.key === 'subscriptionStatus' || e.key === 'userPlan') {
        checkSubscriptionStatus();
        loadRemainingAnalyses();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    const handleSubscriptionUpdate = () => {
      checkSubscriptionStatus();
      loadRemainingAnalyses();
    };
    
    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    window.addEventListener('subscriptionChange', handleSubscriptionUpdate);
    
    const handleFocus = () => {
      checkSubscriptionStatus();
      loadRemainingAnalyses();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
      window.removeEventListener('subscriptionChange', handleSubscriptionUpdate);
      window.removeEventListener('focus', handleFocus);
      if (batchTimerRef.current) {
        clearInterval(batchTimerRef.current);
      }
    };
  }, []);

  const loadUserEmail = async () => {
    try {
      const response = await authService.getProfile();
      const userData = response?.data?.user || response?.user || response;
      const email = userData?.email || '';
      setUserEmail(email);
      
      // Check if user is a Novalink employee
      const isNovalink = email.toLowerCase().endsWith('@novalink-solutions.com');
      setIsNovalinkEmployee(isNovalink);
    } catch (error) {
      console.error('Error loading user email:', error);
    }
  };

  // Update subscription status when Novalink employee status changes
  useEffect(() => {
    if (isNovalinkEmployee) {
      setSubscriptionActive(true);
      setRemainingAnalyses('unlimited');
      setUserPlan('Pro');
    } else {
      checkSubscriptionStatus();
      loadRemainingAnalyses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNovalinkEmployee]);

  // Reset form when navigating with reset state
  useEffect(() => {
    if (location.state?.reset) {
      handleNewScan();
      // Clear the state to prevent resetting on every render
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const checkSubscriptionStatus = () => {
    // Novalink employees always have active subscription
    if (isNovalinkEmployee) {
      setSubscriptionActive(true);
      return;
    }
    
    const subscriptionStatus = localStorage.getItem('subscriptionStatus');
    setSubscriptionActive(subscriptionStatus !== 'canceled');
  };

  const loadRemainingAnalyses = async () => {
    try {
      // Novalink employees always have unlimited analyses
      if (isNovalinkEmployee) {
        setRemainingAnalyses('unlimited');
        setUserPlan('Pro');
        setSubscriptionActive(true);
        return;
      }
      
      const subscriptionStatus = localStorage.getItem('subscriptionStatus');
      if (subscriptionStatus === 'canceled') {
        setRemainingAnalyses(0);
        setUserPlan('None');
        return;
      }

      const mockPlan = localStorage.getItem('userPlan') || 'Basic';
      setUserPlan(mockPlan);
      
      const storedRemaining = localStorage.getItem('remainingAnalyses');
      if (storedRemaining === 'unlimited') {
        setRemainingAnalyses('unlimited');
      } else if (storedRemaining) {
        setRemainingAnalyses(parseInt(storedRemaining) || 0);
      } else {
        if (mockPlan === 'Pro') {
          setRemainingAnalyses('unlimited');
        } else {
          const dailyLimit = mockPlan === 'Basic' ? 10 : 20;
          const todayAnalyses = parseInt(localStorage.getItem('todayAnalyses') || '0');
          setRemainingAnalyses(Math.max(0, dailyLimit - todayAnalyses));
        }
      }
    } catch (error) {
      console.error('Error loading remaining analyses:', error);
      setRemainingAnalyses(0);
    }
  };


  const handleNewScan = () => {
    // Clear current form
    setResume(null);
    setResumeText("");
    setJobDescription("");
    setResults(null);
    setBatchComparison(null);
    setBatchSelectedResumeId(null);
    setBatchSelectedResumeIds([]);
    setBatchUploadFiles([]);
    setShowBatchSelectModal(false);
    setError(null);
    setAnalysisMode(null);
    setShowInputSections(true);
  };

  // Handle scan history selection
  const handleSelectScan = (scan) => {
    if (!scan) return;

    const batchPayload = scan.aiAnalysis?.kind === 'batch_compare' ? scan.aiAnalysis : null;
    if (batchPayload && batchPayload.comparison && Array.isArray(batchPayload.results)) {
      setResults(null);
      setBatchComparison({
        comparison: batchPayload.comparison,
        results: batchPayload.results,
        performance: batchPayload.performance,
        jobDescription: batchPayload.jobDescription || scan.inputData?.jobDescription || ''
      });
      setJobDescription(batchPayload.jobDescription || scan.inputData?.jobDescription || '');
      setBatchSelectedResumeId(
        batchPayload.comparison?.topResume?.resumeId ||
          batchPayload.comparison?.ranking?.[0]?.resumeId ||
          batchPayload.results?.find((r) => r?.success)?.resumeId ||
          null
      );
      setAnalysisMode('batch');
      setShowInputSections(false);
      setShowHistoryModal(false);
      return;
    }

    if (scan && scan.analysisResults) {
      // COMPREHENSIVE normalization - ensure ALL field name variations exist
      const normalizeKeywordMatches = (keywordMatches) => {
        if (!keywordMatches) {
          console.warn('⚠️ No keywordMatches in historical scan');
          return {
            mustHaveCovered: [],
            criticalMatchedSkills: [],
            mustHaveMissing: [],
            criticalMissingSkills: [],
            niceToHaveSkills: [],
            niceToHaveMatched: [],
            niceToHaveCovered: [],
            coveragePercent: 0
          };
        }

        // Get actual data from whatever field exists
        const matchedSkills = keywordMatches.mustHaveCovered || keywordMatches.criticalMatchedSkills || [];
        const missingSkills = keywordMatches.mustHaveMissing || keywordMatches.criticalMissingSkills || [];
        const niceToHave = keywordMatches.niceToHaveSkills || keywordMatches.niceToHaveMatched || keywordMatches.niceToHaveCovered || [];

        // Return object with ALL possible field name variations
        return {
          ...keywordMatches,
          // New field names
          mustHaveCovered: matchedSkills,
          mustHaveMissing: missingSkills,
          niceToHaveSkills: niceToHave,
          niceToHaveCovered: niceToHave,
          niceToHaveMatched: niceToHave,
          // Old field names (for backward compatibility)
          criticalMatchedSkills: matchedSkills,
          criticalMissingSkills: missingSkills,
          // Ensure coveragePercent exists
          coveragePercent: keywordMatches.coveragePercent || 0
        };
      };

      // Convert scan history data back to the format expected by NovaMatchResult
      const formattedResult = {
        ...scan.analysisResults,
        jdText: scan.inputData?.jobDescription || '',
        raw: scan.processingMetadata || {},
        // Normalize keywordMatches to ensure compatibility
        keywordMatches: normalizeKeywordMatches(scan.analysisResults.keywordMatches)
      };

      setResults(formattedResult);
      setShowHistoryModal(false);
    }
  };

  // Handle view all scans
  const handleViewAllScans = () => {
    setShowHistoryModal(true);
  };

  const handlePreviewResume = () => {
    const content = inputType === 0 ? resumePreview : resumeText;
    setPreviewContent(content);
    setShowPreviewModal(true);
  };

  // Handle tab change between file and text input
  const handleTabChange = (event, newValue) => {
    setInputType(newValue);
    setResume(null);
    setResumeText("");
    setResumePreview("");
    setError(null);
  };

  // Handle file upload and preview
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setResume(file);
    setResumeText("");
    setError("");
    if (file && file.type.startsWith("text")) {
      const reader = new FileReader();
      reader.onload = (ev) => setResumePreview(ev.target.result.slice(0, 30000));
      reader.readAsText(file);
    } else {
      setResumePreview("");
    }
  };

  // Handle text input change
  const handleTextChange = (e) => {
    setResumeText(e.target.value);
    setResume(null);
    setResumePreview(e.target.value.slice(0, 30000));
    setError(null);
  };

  // Drag-and-drop support
  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange({ target: { files: e.dataTransfer.files } });
    }
  };

  const handleRemoveFile = () => {
    setResume(null);
    setResumePreview("");
  };

  const handleClearAll = () => {
    setInputType(0);
    setResume(null);
    setResumeText("");
    setResumePreview("");
    setJobDescription("");
    setResults(null);
    setBatchComparison(null);
    setBatchSelectedResumeId(null);
    setBatchSelectedResumeIds([]);
    setBatchUploadFiles([]);
    setShowBatchSelectModal(false);
    setError(null);
    setCurrentStep(0);
    setAnalysisMode(null);
    setShowInputSections(true);
  };

  const handleSelectAnalysisMode = (mode) => {
    setAnalysisMode(mode);
    setError(null);
    setResults(null);
    setBatchComparison(null);
    setBatchSelectedResumeId(null);
    setBatchSelectedResumeIds([]);
    setBatchUploadFiles([]);
    setShowBatchSelectModal(false);
  };

  const loadResumeLibraryForBatch = async () => {
    if (resumeLibraryLoading) return;
    setResumeLibraryError(null);
    setResumeLibraryLoading(true);
    try {
      const token = localStorage.getItem('novamatch_auth_token');
      if (!token) {
        throw new Error('Please log in to view your resumes');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/user/resumes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(errorText || 'Failed to load resume library');
      }

      const data = await response.json();
      setResumeLibrary(Array.isArray(data?.resumes) ? data.resumes : []);
    } catch (err) {
      setResumeLibraryError(err.message || 'Failed to load resumes');
    } finally {
      setResumeLibraryLoading(false);
    }
  };

  const toggleBatchResume = (resumeId) => {
    setBatchSelectedResumeIds((prev) => {
      if (prev.includes(resumeId)) return prev.filter((id) => id !== resumeId);
      if (prev.length >= 5) return prev;
      return [...prev, resumeId];
    });
  };

  /** Stops waiting on the current batch (in-flight HTTP). Server work may continue until complete. */
  const handleCancelBatchScan = () => {
    batchAbortRef.current?.abort();
  };

  const stopBatchTimer = () => {
    if (batchTimerRef.current) {
      clearInterval(batchTimerRef.current);
      batchTimerRef.current = null;
    }
  };

  const startBatchTimer = () => {
    stopBatchTimer();
    batchStartedAtRef.current = Date.now();
    setBatchElapsedMs(0);
    batchTimerRef.current = setInterval(() => {
      if (!batchStartedAtRef.current) return;
      setBatchElapsedMs(Date.now() - batchStartedAtRef.current);
    }, 1000);
  };

  const formatElapsed = (valueMs) => {
    const totalSeconds = Math.max(0, Math.floor((valueMs || 0) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const runBatchCompareWithProgress = async ({ resumeIds, jobDescription, signal, token }) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/user/resumes/compare-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ resumeIds, jobDescription }),
      signal
    });

    if (!response.ok || !response.body) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.error || errorData?.details || 'Batch comparison failed');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    const applyEvent = (eventData) => {
      if (typeof eventData?.progress === 'number') {
        setBatchProgress(Math.max(0, Math.min(100, eventData.progress)));
      }
      if (eventData?.message) {
        setBatchProgressMessage(eventData.message);
      }
      if (eventData?.stage) {
        setBatchStage(eventData.stage);
      }
      if (eventData?.metrics) {
        setBatchProgressMetrics((prev) => ({
          processedCount: Number.isFinite(Number(eventData.metrics.processedCount))
            ? Number(eventData.metrics.processedCount)
            : prev.processedCount,
          totalCount: Number.isFinite(Number(eventData.metrics.totalCount))
            ? Number(eventData.metrics.totalCount)
            : prev.totalCount,
          currentResumeName: eventData.metrics.currentResumeName || prev.currentResumeName
        }));
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';

        for (const chunk of chunks) {
          const lines = chunk.split('\n');
          const dataLines = lines
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.replace(/^data:\s?/, ''));

          if (dataLines.length === 0) continue;
          let eventData = null;
          try {
            eventData = JSON.parse(dataLines.join('\n'));
          } catch (_) {
            continue;
          }

          applyEvent(eventData);

          if (eventData?.stage === 'error') {
            throw new Error(eventData.error || 'Batch comparison failed');
          }

          if (eventData?.stage === 'complete' && eventData?.result) {
            return eventData.result;
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    throw new Error('Batch progress stream ended before completion');
  };

  /** Close batch modal; if a scan is running, abort in-flight requests first. */
  const handleBatchModalDismiss = () => {
    if (loading) {
      handleCancelBatchScan();
    }
    setShowBatchSelectModal(false);
  };

  const handleBatchCompare = async () => {
    if (!jobDescription.trim()) {
      setError({ error: 'Please enter a job description first' });
      return;
    }

    const files = batchUploadFiles || [];
    const savedIds = batchSelectedResumeIds || [];

    if (files.length + savedIds.length > 5) {
      setError({ error: 'You can select at most 5 resumes total (uploaded files plus saved).' });
      return;
    }
    if (files.length + savedIds.length < 2) {
      setError({ error: 'Select at least 2 resumes to run a batch scan.' });
      return;
    }

    const abortController = new AbortController();
    batchAbortRef.current = abortController;
    const { signal } = abortController;

    // Saved-library only: no new uploads in this run
    if (files.length === 0) {
      if (savedIds.length < 2) {
        setError({ error: 'Select at least 2 resumes to run a batch scan.' });
        return;
      }
      if (savedIds.length > 5) {
        setError({ error: 'You can select up to 5 resumes for batch scanning.' });
        return;
      }

      setLoading(true);
      setBatchProgress(8);
      setBatchProgressMessage('Preparing batch comparison...');
      setBatchStage('validating');
      setBatchProgressMetrics({ processedCount: 0, totalCount: savedIds.length, currentResumeName: '' });
      setError(null);
      setResults(null);
      setBatchComparison(null);
      setCurrentStep(0);
      setProgress(0);
      setProgressMessage('Starting batch scan...');
      setShowInputSections(false);
      startBatchTimer();

      try {
        const token = localStorage.getItem('novamatch_auth_token');
        setBatchProgress(25);
        setBatchProgressMessage(`Submitting ${savedIds.length} saved resume${savedIds.length === 1 ? '' : 's'} for AI analysis...`);
        const responseData = await runBatchCompareWithProgress({
          resumeIds: savedIds,
          jobDescription,
          signal,
          token
        });

        setBatchComparison(responseData);
        setBatchProgress(95);
        setBatchProgressMessage('Building ranked results...');
        const topId =
          responseData?.comparison?.topResume?.resumeId ||
          responseData?.comparison?.ranking?.[0]?.resumeId ||
          responseData?.results?.find((r) => r?.success)?.resumeId ||
          null;
        setBatchSelectedResumeId(topId);
        setBatchProgress(100);
        setBatchProgressMessage('Batch analysis complete.');
        setShowBatchSelectModal(false);
        window.dispatchEvent(new CustomEvent('scanHistoryUpdated'));
      } catch (err) {
        if (err?.name === 'AbortError') {
          setError({ error: 'Batch scan cancelled.' });
        } else {
          setError({ error: err.message || 'Batch comparison failed' });
        }
        setBatchProgressMessage('');
        setBatchProgress(0);
        setBatchStage('');
        setBatchProgressMetrics({ processedCount: 0, totalCount: 0, currentResumeName: '' });
      } finally {
        stopBatchTimer();
        setLoading(false);
        batchAbortRef.current = null;
      }
      return;
    }

    // One or more file uploads (optional merge with saved selections)
    setLoading(true);
    setBatchProgress(5);
    setBatchProgressMessage('Preparing uploads...');
    setBatchStage('loading_resumes');
    setBatchProgressMetrics({ processedCount: 0, totalCount: files.length + savedIds.length, currentResumeName: '' });
    setError(null);
    setResults(null);
    setBatchComparison(null);
    setCurrentStep(0);
    setProgress(0);
    setProgressMessage('Starting batch scan (upload)...');
    startBatchTimer();

    try {
      const token = localStorage.getItem('novamatch_auth_token');
      if (!token) throw new Error('Please log in again');

      const resumeIds = [];
      const seen = new Set();
      const pushId = (id) => {
        if (!id || seen.has(id)) return;
        seen.add(id);
        resumeIds.push(id);
      };

      for (const file of files) {
        const uploadBase = 10;
        const uploadSpan = 45;
        const currentPercent = uploadBase + Math.round((resumeIds.length / Math.max(files.length, 1)) * uploadSpan);
        setBatchProgress(Math.min(currentPercent, 55));
        setBatchProgressMessage(`Uploading ${resumeIds.length + 1}/${files.length}: ${file.name}`);

        if (file.size > MAX_RESUME_UPLOAD_BYTES) {
          throw new Error(
            `${file.name} is over ${MAX_RESUME_UPLOAD_MB}MB. Choose a smaller file or raise MAX_RESUME_FILE_SIZE_MB on the server.`
          );
        }

        const uploadForm = new FormData();
        uploadForm.append('resume', file);
        const defaultName = (file.name || 'resume').replace(/\.[^/.]+$/, '');
        uploadForm.append('name', defaultName);

        const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/user/resumes/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: uploadForm,
          signal
        });

        const uploadData = await uploadResponse.json().catch(() => ({}));

        if (uploadResponse.status === 409 && uploadData?.existingResumeId) {
          pushId(uploadData.existingResumeId);
          setBatchProgressMessage(`Linked existing resume for ${file.name}`);
          continue;
        }

        if (!uploadResponse.ok) {
          const base = uploadData?.error || uploadData?.details || `Upload failed for ${file.name}`;
          if (uploadResponse.status === 413) {
            throw new Error(`${file.name}: ${base}`);
          }
          throw new Error(base);
        }

        if (uploadData?.resumeId) {
          pushId(uploadData.resumeId);
        }
      }

      setBatchProgress(60);
      setBatchProgressMessage('Preparing selected resumes for comparison...');
      for (const id of savedIds) {
        if (resumeIds.length >= 5) break;
        pushId(id);
      }

      if (resumeIds.length < 2) {
        throw new Error(
          'Need at least 2 distinct resumes for a batch scan. Duplicate uploads count once; add another resume or pick one from Saved.'
        );
      }

      setBatchProgress(70);
      setBatchProgressMessage(`Running AI analysis for ${resumeIds.length} candidate${resumeIds.length === 1 ? '' : 's'}...`);
      const responseData = await runBatchCompareWithProgress({
        resumeIds,
        jobDescription,
        signal,
        token
      });

      setBatchComparison(responseData);
      setBatchProgress(95);
      setBatchProgressMessage('Building ranked results...');
      const topId =
        responseData?.comparison?.topResume?.resumeId ||
        responseData?.comparison?.ranking?.[0]?.resumeId ||
        responseData?.results?.find((r) => r?.success)?.resumeId ||
        null;
      setBatchSelectedResumeId(topId);

      await loadResumeLibraryForBatch();
      setBatchProgress(100);
      setBatchProgressMessage('Batch analysis complete.');
      setShowBatchSelectModal(false);
      window.dispatchEvent(new CustomEvent('scanHistoryUpdated'));
    } catch (err) {
      if (err?.name === 'AbortError') {
        setError({ error: 'Batch scan cancelled.' });
      } else {
        setError({ error: err.message || 'Batch upload+compare failed' });
      }
      setBatchProgressMessage('');
      setBatchProgress(0);
      setBatchStage('');
      setBatchProgressMetrics({ processedCount: 0, totalCount: 0, currentResumeName: '' });
    } finally {
      stopBatchTimer();
      setLoading(false);
      batchAbortRef.current = null;
    }
  };

  // Map backend progress stages to UI steps
  const mapProgressToStep = (stage, progressPercent) => {
    const stageMap = {
      'validating': 0,
      'extracting_text': 0,
      'text_extracted': 1,
      'checking_cache': 1,
      'preparing_analysis': 1,
      'ai_analysis_started': 2,
      'step_1': 2,
      'step_2': 2,
      'step_3': 3,
      'basic_analysis_complete': 3,
      'calculating_scores': 4,
      'step_4': 4,
      'generating_insights': 5,
      'step_5': 5,
      'step_6': 5,
      'step_7': 5,
      'generating_questions': 5,
      'step_8': 5,
      'analysis_complete': 5,
      'finalizing': 5,
      'complete': 6
    };
    
    return stageMap[stage] !== undefined ? stageMap[stage] : Math.floor((progressPercent / 100) * ANALYSIS_STEPS.length);
  };

  const handleAnalyze = async () => {
    // Validation based on input type
    const hasValidInput = inputType === 0 ? resume : resumeText.trim();
    if (!hasValidInput || !jobDescription.trim()) {
      const inputTypeName = inputType === 0 ? "resume file" : "resume text";
      setError({ error: `Please provide ${inputTypeName} and enter a job description.` });
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setCurrentStep(0);
    setProgress(0);
    setProgressMessage("Starting analysis...");
    setShowInputSections(false); // Hide input sections when analysis starts

    try {
      let file;
      if (inputType === 0) {
        // File upload
        file = resume;
      } else {
        // Text input - create a text file
        const textBlob = new Blob([resumeText], { type: 'text/plain' });
        file = new File([textBlob], 'resume.txt', { type: 'text/plain' });
      }

      // Simulate progress while analysis runs
      const progressInterval = setInterval(() => {
        setCurrentStep(prev => {
          if (prev < ANALYSIS_STEPS.length - 1) {
            return prev + 1;
          }
          return prev;
        });
        setProgress(prev => {
          if (prev < 90) {
            return prev + 10;
          }
          return prev;
        });
      }, 2000);

      // Use the regular analyze API
      const data = await novaMatchAPI.analyze(
        file, 
        jobDescription, 
        [], 
        { save: false, fastMode: false }
      );

      // Clear progress simulation
      clearInterval(progressInterval);

      // Complete the progress
      setCurrentStep(ANALYSIS_STEPS.length);
      setProgress(100);
      setTimeout(() => {
        setResults(normalizeAnalysisV2(data));
      }, 500);

    } catch (err) {
      // Preserve structured error data if available
      if (err.validation || err.code || err.response?.data) {
        setError({
          error: err.message || "An error occurred during analysis.",
          code: err.code,
          validation: err.validation || err.response?.data?.validation,
          ...err.response?.data,
        });
      } else {
        // Fallback for non-structured errors
        setError({
          error: err.message || "An error occurred during analysis.",
        });
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
        setCurrentStep(0);
        setProgress(0);
        setProgressMessage("");
        // Don't reset showInputSections here - let it stay hidden when results are shown
      }, 1000);
    }
  };

  return (
    <Box className="main-container" sx={{
      minHeight: '100vh',
      background: '#fafafa',
      display: 'flex',
      overflowX: 'hidden', // Prevent horizontal scrolling only
      overflowY: 'auto', // Allow vertical scrolling
      width: '100vw' // Ensure full viewport width
    }}>
      {/* Professional Sidebar */}
      <ProfessionalSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onSelectScan={handleSelectScan}
        onViewAllScans={handleViewAllScans}
      />

      {/* Main Content Area */}
      <Box className="main-content" sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        marginLeft: { xs: 0, lg: sidebarOpen ? '320px' : '0' },
        transition: 'margin-left 0.3s ease',
        width: { xs: '100vw', lg: sidebarOpen ? 'calc(100vw - 320px)' : '100vw' },
        minWidth: 0, // Prevent flex item from growing beyond container
        overflowX: 'hidden', // Prevent horizontal scrolling only
        overflowY: 'auto' // Allow vertical scrolling
      }}>
        {/* Top Navigation Bar */}
        <Box sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <IconButton
              onClick={() => setSidebarOpen(!sidebarOpen)}
              sx={{
                mr: { xs: 0, sm: 1 },
                color: 'primary.main',
                minWidth: 44,
                minHeight: 44,
                '&:hover': {
                  backgroundColor: 'primary.50'
                }
              }}
              title={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            >
              {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
            <Typography variant="h5" fontWeight={700} color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              NovaMatch
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              AI-Powered Resume Analysis
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1, md: 2 } }}>
            <ScanHistoryDropdown 
              onSelectScan={handleSelectScan}
              onViewAll={handleViewAllScans}
            />
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setShowDocAnalysis(true)}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none', 
                px: { xs: 1, sm: 1.5 }, 
                py: 0.5, 
                fontSize: { xs: 11, sm: 13 }, 
                minHeight: 44,
                minWidth: 44,
                display: { xs: 'none', sm: 'inline-flex' }
              }}
            >
              📄 Document Analysis
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                ref={subscriptionButtonRef}
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSubscriptionMenuOpen}
                endIcon={<ArrowDropDown />}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none', 
                  px: 2, 
                  py: 0.5, 
                  fontSize: 13, 
                  minHeight: 32,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Subscription
              </Button>
              {remainingAnalyses !== null && (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Star
                    sx={{
                      fontSize: 28,
                      color: '#FFD700',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                    }}
                  />
                  <Typography
                    sx={{
                      position: 'absolute',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: '#2c3e50',
                      textAlign: 'center',
                      lineHeight: 1,
                    }}
                  >
                    {remainingAnalyses === 'unlimited' ? '∞' : remainingAnalyses}
                  </Typography>
                </Box>
              )}
            </Box>
            <Menu
              anchorEl={subscriptionAnchorEl}
              open={Boolean(subscriptionAnchorEl)}
              onClose={handleSubscriptionMenuClose}
              PaperProps={{
                elevation: 8,
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem 
                onClick={handleBillingClick}
                sx={{
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    bgcolor: 'primary.50',
                    '& .MuiSvgIcon-root': {
                      color: 'primary.main'
                    }
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <Payment sx={{ mr: 2, fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2" fontWeight={500}>
                  Billing
                </Typography>
              </MenuItem>
              <MenuItem 
                onClick={handleInvoiceClick}
                sx={{
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    bgcolor: 'primary.50',
                    '& .MuiSvgIcon-root': {
                      color: 'primary.main'
                    }
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <Receipt sx={{ mr: 2, fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2" fontWeight={500}>
                  Invoice
                </Typography>
              </MenuItem>
              <MenuItem 
                onClick={handleHelpClick}
                sx={{
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    bgcolor: 'primary.50',
                    '& .MuiSvgIcon-root': {
                      color: 'primary.main'
                    }
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <HelpOutline sx={{ mr: 2, fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2" fontWeight={500}>
                  Help
                </Typography>
              </MenuItem>
            </Menu>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleLogout}
              startIcon={<LogoutIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />}
              sx={{ 
                borderRadius: 2, 
                px: { xs: 1, sm: 1.5 }, 
                py: 0.5, 
                fontSize: { xs: 11, sm: 13 }, 
                minHeight: 44,
                minWidth: 44,
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } } 
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Logout</Box>
              <LogoutIcon sx={{ display: { xs: 'inline-block', sm: 'none' } }} />
            </Button>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ 
          flex: 1, 
          overflowX: 'hidden', // Prevent horizontal scrolling
          overflowY: 'auto', // Allow vertical scrolling
          width: '100%',
          maxWidth: '100%'
        }}>
        {batchComparison ? (
          // Full Screen Batch Results View
          <Box sx={{ p: 0, height: '100%', width: '100%', maxWidth: '100%', overflowX: 'hidden', overflowY: 'auto' }}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h5" fontWeight={800} color="primary.main">
                  Candidate Batch Rankings
                </Typography>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleNewScan}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    bgcolor: 'success.main',
                    '&:hover': { bgcolor: 'success.dark', transform: 'translateY(-1px)' },
                    transition: 'all 0.2s ease'
                  }}
                >
                  🔄 New Batch Scan
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Click a candidate rank to view the full singular-style assessment.
              </Typography>
            </Box>

            <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(batchComparison?.comparison?.ranking || []).map((item) => {
                  const isSelected = item.resumeId === batchSelectedResumeId;
                  return (
                    <Box
                      key={item.resumeId}
                      onClick={() => setBatchSelectedResumeId(item.resumeId)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: isSelected ? '2px solid rgba(33,150,243,0.9)' : '1px solid rgba(0,0,0,0.12)',
                        bgcolor: isSelected ? 'rgba(33,150,243,0.08)' : 'background.paper',
                        p: 1.5
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                          Rank #{item.rank} • {item.resumeName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          Score: {Math.round(item.score || 0)} • Coverage: {Math.round(item.coveragePercent || 0)}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {(() => {
              const selectedEntry = batchComparison?.results?.find(
                (r) => r?.success && r?.resumeId === batchSelectedResumeId
              );
              const effectiveEntry =
                selectedEntry ||
                batchComparison?.results?.find((r) => r?.success) ||
                null;

              if (!effectiveEntry?.result) {
                return (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No successful candidate analyses found.
                    </Typography>
                  </Box>
                );
              }

              const effectiveResult = effectiveEntry.result;
              const effectiveMustHave =
                effectiveResult?.keywordMatches?.mustHaveCovered ||
                effectiveResult?.keywordMatches?.criticalMatchedSkills ||
                [];

              return (
                <Box sx={{ p: 0, height: 'auto', overflowX: 'hidden' }}>
                  <NovaMatchResult
                    result={effectiveResult}
                    analysisId={effectiveEntry.analysisId}
                    resumeContent={effectiveResult?.resumeText || resumePreview || resumeText}
                    mustHaveSkills={effectiveMustHave}
                    onNewAssessment={handleNewScan}
                  />
                </Box>
              );
            })()}
          </Box>
        ) : results ? (
          // Full Screen Results View
          <Box sx={{ 
            p: 0, 
            height: '100%',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden', // Prevent horizontal scrolling
            overflowY: 'auto' // Allow vertical scrolling
          }}>
            <NovaMatchResult
              result={results}
              analysisId={results.analysisId}
              resumeContent={resumePreview || resumeText}
              mustHaveSkills={results?.keywords?.jdMustHave || []}
              onNewAssessment={handleNewScan}
            />
          </Box>
        ) : (
          // Input Form View
          <Container maxWidth="xl" sx={{ py: { xs: 1.5, sm: 2 } }}>

            <Paper elevation={3} sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              borderRadius: 3,
              boxShadow: '0 4px 20px 0 rgba(31, 38, 135, 0.1)',
              bgcolor: 'white',
              position: 'relative'
            }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 1.5, fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' } }}>
                  New Candidate Assessment
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' }, px: { xs: 1, sm: 0 } }}>
                  Upload a resume and paste a job description to get an instant AI-powered assessment
                </Typography>
              </Box>

              {/* Input Section - Conditionally rendered */}
              {showInputSections && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: { xs: 2, sm: 2.5 },
                  transition: 'all 0.5s ease-in-out',
                  opacity: showInputSections ? 1 : 0,
                  transform: showInputSections ? 'translateY(0)' : 'translateY(-20px)',
                  overflow: 'hidden',
                  position: 'relative',
                  filter: (!subscriptionActive && !isNovalinkEmployee) ? 'grayscale(100%)' : 'none',
                  pointerEvents: (!subscriptionActive && !isNovalinkEmployee) ? 'none' : 'auto'
                }}>
                {!analysisMode && (
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                      Choose Analysis Type
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Select your flow first. You can switch anytime.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={() => handleSelectAnalysisMode('single')}
                        sx={{ borderRadius: 2, fontWeight: 700, py: 1.5, minHeight: 48 }}
                      >
                        🚀 Single Analysis
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleSelectAnalysisMode('batch')}
                        sx={{ borderRadius: 2, fontWeight: 700, py: 1.5, minHeight: 48 }}
                      >
                        🧠 Batch Analysis (up to 5)
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Top Section - Resume Input (single flow only) */}
                {analysisMode === 'single' && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                    1. Resume Input
                  </Typography>

                  {/* Input Method Tabs */}
                  <Paper sx={{ width: '100%', borderRadius: 3 }}>
              <Tabs
                value={inputType}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  borderRadius: '12px 12px 0 0',
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  }
                }}
              >
                <Tab
                  icon={<ArticleIcon />}
                  label="Upload File"
                  iconPosition="start"
                  sx={{ fontWeight: 600, textTransform: 'none' }}
                />
                <Tab
                  icon={<EditIcon />}
                  label="Paste Text"
                  iconPosition="start"
                  sx={{ fontWeight: 600, textTransform: 'none' }}
                />
              </Tabs>

              {/* File Upload Tab */}
              {inputType === 0 && (
                <Box sx={{ p: 2 }}>
                  <Box
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    sx={{
                      border: '2px dashed #90caf9',
                      borderRadius: 2,
                      p: 2.5,
                      textAlign: 'center',
                      width: '100%',
                      minHeight: '140px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      bgcolor: resume ? '#e3f2fd' : '#f5faff',
                      transition: 'background 0.2s',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#e3f2fd' }
                    }}
                  >
                    <CloudUploadIcon color="primary" sx={{ fontSize: 36, mb: 0.5 }} />
                    <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>Click to upload or drag and drop</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                      PDF, DOCX, TXT, RTF
                    </Typography>
                    <Button variant="contained" component="label" size="medium" sx={{ borderRadius: 2, fontWeight: 600, fontSize: 14, py: 1, px: 3 }}>
                      Select File
                      <input
                        type="file"
                        accept=".pdf,.docx,.txt,.rtf"
                        hidden
                        onChange={handleFileChange}
                      />
                    </Button>
                    {resume && (
                      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="subtitle1" fontWeight={600}>{resume.name}</Typography>
                          <Button size="small" color="error" onClick={handleRemoveFile} startIcon={<DeleteIcon />} sx={{ borderRadius: 2 }}>Remove</Button>
                        </Box>
                        {resumePreview && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                              <Chip
                                label={`${resumePreview.length} characters`}
                                size="small"
                                color={resumePreview.length > 30000 ? "error" : resumePreview.length > 20000 ? "warning" : "primary"}
                                variant="outlined"
                              />
                              <Chip
                                label={`${resumePreview.split(/\s+/).filter(word => word.length > 0).length} words`}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                              {resumePreview.length > 30000 && (
                                <Chip
                                  label="File content too long for optimal AI processing"
                                  size="small"
                                  color="error"
                                  variant="filled"
                                />
                              )}
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handlePreviewResume}
                              sx={{ borderRadius: 2 }}
                            >
                              Preview Resume
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Text Input Tab */}
              {inputType === 1 && (
                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <TextField
                    label="Resume Content"
                    multiline
                    rows={8}
                    value={resumeText}
                    onChange={handleTextChange}
                    placeholder="Paste your resume content here..."
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: { xs: 13, sm: 14 }
                      }
                    }}
                    InputProps={{
                      style: { borderRadius: 8, minHeight: 48 }
                    }}
                  />
                  {resumeText && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${resumeText.length} characters`}
                          size="small"
                          color={resumeText.length > 30000 ? "error" : resumeText.length > 20000 ? "warning" : "primary"}
                          variant="outlined"
                        />
                        <Chip
                          label={`${resumeText.split(/\s+/).filter(word => word.length > 0).length} words`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                        {resumeText.length > 30000 && (
                          <Chip
                            label="Text too long for optimal AI processing"
                            size="small"
                            color="error"
                            variant="filled"
                          />
                        )}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handlePreviewResume}
                        sx={{ borderRadius: 2, alignSelf: 'flex-start' }}
                      >
                        Preview Resume
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
                </Box>
                )}

                {/* Bottom Section - Job Description */}
                {analysisMode && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                    {analysisMode === 'single' ? '2. Job Description' : '1. Job Description'}
                  </Typography>

                  <TextField
                    label="Job Description"
                    multiline
                    rows={12}
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the complete job description here..."
                    fullWidth
                    sx={{ 
                      borderRadius: 2, 
                      fontSize: { xs: 13, sm: 14 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                    InputProps={{ 
                      style: { 
                        borderRadius: 8,
                        minHeight: 48
                      } 
                    }}
                  />
                  {jobDescription && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`${jobDescription.length} characters`}
                        size="small"
                        color={jobDescription.length > 30000 ? "error" : jobDescription.length > 20000 ? "warning" : "primary"}
                        variant="outlined"
                      />
                      <Chip
                        label={`${jobDescription.split(/\s+/).filter(word => word.length > 0).length} words`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                      {jobDescription.length > 30000 && (
                        <Chip
                          label="Job description too long"
                          size="small"
                          color="error"
                          variant="filled"
                        />
                      )}
                    </Box>
                  )}
                </Box>
                )}
                </Box>
              )}

              {/* Action Buttons - Always visible */}
              <Box sx={{ 
                mt: { xs: 3, sm: 4 }, 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 }, 
                justifyContent: 'center', 
                pb: { xs: 2, sm: 3 },
                transition: 'all 0.3s ease-in-out'
              }}>
                {!showInputSections && !loading && (
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={handleNewScan}
                    sx={{ 
                      borderRadius: 2, 
                      fontWeight: 600, 
                      fontSize: { xs: 15, sm: 14 }, 
                      py: 1.5, 
                      px: { xs: 3, sm: 4 },
                      minWidth: { xs: '100%', sm: 160 },
                      minHeight: 48,
                      bgcolor: 'success.main',
                      '&:hover': {
                        bgcolor: 'success.dark',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 15px 0 rgba(76,175,80,0.35)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🔄 New Analysis
                  </Button>
                )}
                
                {showInputSections && analysisMode && (
                  <>
                    {analysisMode === 'single' ? (
                      <Button
                        variant="contained"
                        size="medium"
                        disabled={(!resume && !resumeText.trim()) || loading || !jobDescription.trim() || (!subscriptionActive && !isNovalinkEmployee)}
                        onClick={handleAnalyze}
                        sx={{ 
                          borderRadius: 2, 
                          fontWeight: 600, 
                          fontSize: { xs: 15, sm: 16 }, 
                          py: 1.5, 
                          px: { xs: 3, sm: 6 },
                          boxShadow: '0 4px 15px 0 rgba(33,150,243,0.25)',
                          minWidth: { xs: '100%', sm: 280 },
                          minHeight: 48,
                          '&:hover': {
                            boxShadow: '0 6px 20px 0 rgba(33,150,243,0.35)',
                            transform: 'translateY(-1px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "🚀 Begin AI Analysis"}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="medium"
                        disabled={loading || !jobDescription.trim() || (!subscriptionActive && !isNovalinkEmployee)}
                        onClick={() => {
                          setShowBatchSelectModal(true);
                          loadResumeLibraryForBatch();
                        }}
                        sx={{ 
                          borderRadius: 2, 
                          fontWeight: 600, 
                          fontSize: { xs: 15, sm: 16 }, 
                          py: 1.5, 
                          px: { xs: 3, sm: 6 },
                          boxShadow: '0 4px 15px 0 rgba(33,150,243,0.25)',
                          minWidth: { xs: '100%', sm: 280 },
                          minHeight: 48,
                          '&:hover': {
                            boxShadow: '0 6px 20px 0 rgba(33,150,243,0.35)',
                            transform: 'translateY(-1px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        🧠 Open Batch Selector
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="medium"
                      disabled={loading}
                      onClick={() => setAnalysisMode(null)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: { xs: 14, sm: 14 },
                        py: 1.5,
                        px: { xs: 2.5, sm: 3 },
                        minWidth: { xs: '100%', sm: 200 },
                        minHeight: 48
                      }}
                    >
                      Switch Flow
                    </Button>
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={handleClearAll}
                      sx={{ 
                        borderRadius: 2, 
                        fontWeight: 600, 
                        fontSize: { xs: 14, sm: 14 }, 
                        py: 1.5, 
                        px: { xs: 3, sm: 4 },
                        minWidth: { xs: '100%', sm: 140 },
                        minHeight: 48,
                        '&:hover': {
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Clear All
                    </Button>
                  </>
                )}
              </Box>

              {/* Subscription Overlay */}
              {!subscriptionActive && !isNovalinkEmployee && showInputSections && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    borderRadius: 3,
                    pointerEvents: 'auto',
                    gap: 2,
                    p: 4
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      textAlign: 'center',
                      mb: 1
                    }}
                  >
                    Subscription Required
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(44, 62, 80, 0.7)',
                      textAlign: 'center',
                      mb: 2
                    }}
                  >
                    Please subscribe to continue using resume analysis
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/billing')}
                    sx={{
                      bgcolor: '#1976d2',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                      '&:hover': {
                        bgcolor: '#1565c0',
                      },
                    }}
                  >
                    Subscribe Now
                  </Button>
                </Box>
              )}

              {error && (
                <ValidationErrorDisplay
                  error={error}
                  onDismiss={() => setError(null)}
                />
              )}

              {/* Loading State */}
              {loading && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 3,
                  py: 4,
                  textAlign: 'center',
                  animation: 'fadeIn 0.5s ease-in-out'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    mb: 3,
                    p: 2,
                    bgcolor: 'primary.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'primary.200'
                  }}>
                    <CircularProgress size={32} thickness={4} sx={{ color: 'primary.main' }} />
                    <Typography variant="h4" fontWeight={600} color="primary.main" sx={{ fontSize: '1.5rem' }}>
                      🚀 AI Analysis in Progress
                    </Typography>
                  </Box>
                  
                  <Paper sx={{ p: 3, width: '100%', maxWidth: 500, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}>
                    <Stepper activeStep={currentStep} orientation="vertical" sx={{ mb: 2 }}>
                      {ANALYSIS_STEPS.map((step, index) => (
                        <Step key={index} completed={index < currentStep}>
                          <StepLabel
                            StepIconComponent={() => (
                              <Box sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: index < currentStep ? '#4caf50' : index === currentStep ? '#2196f3' : '#e0e0e0',
                                color: 'white',
                                fontSize: '16px',
                                boxShadow: index <= currentStep ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                                transition: 'all 0.3s ease'
                              }}>
                                {index < currentStep ? <CheckCircleIcon sx={{ fontSize: 20 }} /> : step.icon}
                              </Box>
                            )}
                          >
                            <Typography 
                              variant="body1" 
                              fontWeight={index <= currentStep ? 600 : 400}
                              color={index <= currentStep ? 'text.primary' : 'text.secondary'}
                              sx={{ ml: 1.5, fontSize: '0.9rem' }}
                            >
                              {step.label}
                            </Typography>
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                    
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="body1" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                          {progressMessage || 'Processing your analysis...'}
                        </Typography>
                        <Typography variant="body1" fontWeight={700} color="primary" sx={{ 
                          fontFamily: 'monospace',
                          fontSize: '1rem'
                        }}>
                          {Math.round(progress)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ 
                          borderRadius: 4, 
                          height: 12,
                          bgcolor: '#e3f2fd',
                          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            background: 'linear-gradient(90deg, #2196f3, #21cbf3, #2196f3)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 2s ease-in-out infinite',
                            boxShadow: '0 1px 6px rgba(33, 150, 243, 0.3)'
                          }
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: 1.5, 
                      p: 2, 
                      bgcolor: '#f8f9fa', 
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      animation: currentStep < ANALYSIS_STEPS.length ? 'pulse 2s ease-in-out infinite' : 'none'
                    }}>
                      <CircularProgress 
                        size={24} 
                        thickness={4} 
                        sx={{ 
                          color: 'primary.main',
                          animation: 'pulse 1.5s ease-in-out infinite'
                        }} 
                      />
                      <Typography variant="body1" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                        {currentStep < ANALYSIS_STEPS.length ? ANALYSIS_STEPS[currentStep]?.label : 'Completing analysis...'}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              )}
            </Paper>
          </Container>
        )}
        </Box>

      {/* Scan History Modal */}
      <ScanHistoryModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        onSelectScan={handleSelectScan}
      />

      {/* Document Analysis Modal */}
      <Modal
        open={showDocAnalysis}
        onClose={() => setShowDocAnalysis(false)}
        aria-labelledby="document-analysis-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '98%', md: '95%' },
          maxWidth: 1400,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'white'
          }}>
            <Typography variant="h6" fontWeight={700}>
              📊 Document Analysis
            </Typography>
            <Button
              onClick={() => setShowDocAnalysis(false)}
              sx={{ color: 'white', minWidth: 'auto', p: 1 }}
            >
              ✕
            </Button>
          </Box>
          <Box sx={{ p: 2, overflow: 'auto', flex: 1, bgcolor: '#f8f9fa' }}>
            {results ? (
              <SkillHighlighting
                scanData={{
                  ...results,
                  resumeText: (resumePreview || resumeText || ''),
                  jobDescription: (jobDescription || results?.jdText || ''),
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Upload a resume and job description to view analysis.
              </Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Resume Preview Modal */}
      <Modal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        aria-labelledby="resume-preview-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', md: '80%' },
          maxWidth: 800,
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'white'
          }}>
            <Typography variant="h6" fontWeight={700}>
              Resume Preview
            </Typography>
            <Button
              onClick={() => setShowPreviewModal(false)}
              sx={{ color: 'white', minWidth: 'auto', p: 1 }}
            >
              ✕
            </Button>
          </Box>
          <Box sx={{
            p: 3,
            overflow: 'auto',
            flex: 1,
            bgcolor: '#f8f9fa'
          }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                fontSize: '0.9rem',
                lineHeight: 1.6,
                fontFamily: 'monospace',
                bgcolor: 'white',
                p: 2,
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}
            >
              {previewContent}
            </Typography>
          </Box>
        </Box>
      </Modal>

      {/* Batch scan: centered modal panel */}
      <Modal
        open={showBatchSelectModal}
        onClose={handleBatchModalDismiss}
        aria-labelledby="batch-select-modal"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '96vw', sm: 'min(96vw, 560px)', md: 'min(94vw, 900px)' },
            maxWidth: '100vw',
            maxHeight: '92vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
        <Box
          sx={{
            p: 2.5,
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'white'
          }}
        >
          <Typography id="batch-select-modal" variant="h6" fontWeight={800}>
            🧠 Batch Scan: Select up to 5 Resumes
          </Typography>
          <IconButton onClick={handleBatchModalDismiss} sx={{ color: 'white' }} aria-label="Close batch scan">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2.5 }}>
          {resumeLibraryError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resumeLibraryError}
            </Alert>
          )}

          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            Upload Resumes (optional, up to 5)
          </Typography>
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1.5,
              mb: 2,
              bgcolor: 'background.paper'
            }}
          >
            <Button
              variant="outlined"
              component="label"
              sx={{ borderRadius: 2, fontWeight: 800 }}
              disabled={resumeLibraryLoading}
            >
              Select up to 5 files
              <input
                type="file"
                accept=".pdf,.docx,.txt,.rtf"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  const sliced = files.slice(0, 5);
                  setBatchUploadFiles(sliced);
                }}
              />
            </Button>
            {batchUploadFiles && batchUploadFiles.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Selected: {batchUploadFiles.length} file(s)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                  {batchUploadFiles.map((f, idx) => (
                    <Typography key={`${f.name}-${idx}`} variant="caption" color="text.secondary">
                      • {f.name}
                    </Typography>
                  ))}
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="text"
                    onClick={() => setBatchUploadFiles([])}
                    sx={{ color: 'error.main', fontWeight: 800 }}
                  >
                    Clear upload selection
                  </Button>
                </Box>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Max {MAX_RESUME_UPLOAD_MB}MB per file. If a file is already in your library, it is linked automatically (no duplicate upload).
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Using saved resumes selection: {batchSelectedResumeIds.length} / 5
          </Typography>

          {resumeLibraryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {resumeLibrary.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No resumes found. Upload a resume first.
                </Typography>
              ) : (
                resumeLibrary.map((r) => {
                  const checked = batchSelectedResumeIds.includes(r.id);
                  return (
                    <Box
                      key={r.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        border: '1px solid',
                        borderColor: checked ? 'rgba(33,150,243,0.6)' : 'divider',
                        borderRadius: 2,
                        p: 1.25,
                        bgcolor: checked ? 'rgba(33,150,243,0.06)' : 'background.paper'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleBatchResume(r.id)}
                          inputProps={{ 'aria-label': `select-${r.name}` }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={800} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.filename} • {r.textLength ? `${Math.round(r.textLength / 1000)}k chars` : 'Text ready'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          )}

          {loading && (
            <Box
              sx={{
                mt: 2,
                mb: 2,
                p: 1.75,
                border: '1px solid',
                borderColor: 'rgba(33, 150, 243, 0.3)',
                bgcolor: 'rgba(33, 150, 243, 0.06)',
                borderRadius: 2
              }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                {batchProgressMessage || 'Running batch analysis...'}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, batchProgress || 0))}
                sx={{ height: 8, borderRadius: 999, mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {Math.round(Math.max(0, Math.min(100, batchProgress || 0)))}% complete
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Elapsed: {formatElapsed(batchElapsedMs)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Current step: {BATCH_STAGE_LABELS[batchStage] || 'Running batch analysis'}
              </Typography>
              {batchProgressMetrics.totalCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Progress: {batchProgressMetrics.processedCount}/{batchProgressMetrics.totalCount} resumes analyzed
                </Typography>
              )}
              {batchProgressMetrics.currentResumeName && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Current: {batchProgressMetrics.currentResumeName}
                </Typography>
              )}
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {BATCH_LOADING_STEPS.map((step, index) => {
                  const threshold = (index + 1) * (100 / BATCH_LOADING_STEPS.length);
                  const done = (batchProgress || 0) >= threshold;
                  return (
                    <Chip
                      key={step}
                      size="small"
                      color={done ? 'success' : 'default'}
                      variant={done ? 'filled' : 'outlined'}
                      label={done ? `✓ ${step}` : step}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            p: 2.5,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25 }}>
            <Button
              variant="outlined"
              onClick={handleBatchModalDismiss}
              sx={{ borderRadius: 2, fontWeight: 700 }}
              fullWidth={true}
            >
              {loading ? 'Cancel batch' : 'Cancel'}
            </Button>
            <Button
              variant="contained"
              onClick={handleBatchCompare}
              disabled={loading || resumeLibraryLoading}
              sx={{
                borderRadius: 2,
                fontWeight: 800,
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark', transform: 'translateY(-1px)' },
                transition: 'all 0.2s ease'
              }}
              fullWidth={true}
            >
              {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Run Batch Scan'}
            </Button>
          </Box>
        </Box>
        </Box>
      </Modal>
      </Box>
    </Box>
  );
};

export default NovaMatch;
